<script setup lang="ts">
  import { computed, provide } from 'vue';
  import { onBeforeRouteLeave } from 'vue-router';
  import { SortOrderInjectionKey } from '@/shared/lib/sortOrder';
  import {
    ColoredButton,
    FieldSearchBar,
    PaginateOrScroll,
    SearchToolbar,
    SelectInput,
  } from '@/shared/ui';
  import ExportExcelIcon from '@/shared/assets/icons/ExportExcel.svg';
  import { useOperationLogsList } from '../model/useOperationLogsList';
  import OperationLogsTable from './OperationLogsTable.vue';
  import { useOperationLogsExport } from '../model/useOperationLogsExport';

  const {
    searchTerm,
    searchFieldId,
    sort,
    order,
    currentPage,
    selectedQuantity,
    filters,
    logs,
    allItems,
    totalLogs,
    totalPages,
    loading,
    showSkeleton,
    isRefreshing,
    loadingMore,
    hasMoreItems,
    error,
    isFilterActive,
    openFilterModal,
    handleSearch,
    handleSortUpdate,
    handlePageChanged,
    buildExportParams,
    formatDate,
    formatTime,
    resetForRouteLeave,
    ORDER_OPTIONS,
    LOG_QUANTITY_OPTIONS,
  } = useOperationLogsList();

  const metadata = computed(() => ({
    page: currentPage.value,
    total: totalLogs.value,
    totalItems: totalLogs.value,
    totalPages: totalPages.value,
  }));

  const hasLogs = computed(() => totalLogs.value > 0);
  const hasActiveSearch = computed(() => searchTerm.value.trim() !== '');
  const hasActiveFilters = computed(() =>
    Object.values(filters.value).some((value) => value !== null && value !== undefined)
  );

  provide(SortOrderInjectionKey, {
    sort,
    order,
  });

  const { handleExportToExcel, isExporting, isExportDisabled } = useOperationLogsExport({
    getExportParams: buildExportParams,
    total: totalLogs,
  });
  onBeforeRouteLeave(resetForRouteLeave);
</script>

<template>
  <div class="pt-4 p-6">
    <h3 class="text-gray-07 text-xl/7.5 font-semibold text-left mb-6">Logs de Operações</h3>

    <div class="flex flex-col gap-4 mb-4">
      <div>
        <ColoredButton
          text="Exportar para Excel"
          :icon="ExportExcelIcon"
          icon-alt="Ícone de exportação"
          :loading="isExporting"
          :disabled="isExportDisabled"
          @click="handleExportToExcel"
        />
      </div>

      <SearchToolbar class="mb-2">
        <template #search>
          <FieldSearchBar
            v-model:search-field-id="searchFieldId"
            :search-fields="[
              { id: 'ipAddress', name: 'IP do Computador' },
              { id: 'description', name: 'Descrição' },
            ]"
            :initial-search-term="searchTerm"
            independent-search-field
            :clear-search-on-field-change="false"
            class="w-full"
            @search="handleSearch"
          />
        </template>
        <template #quantity>
          <SelectInput
            v-model="selectedQuantity"
            :options="LOG_QUANTITY_OPTIONS"
            class="cursor-pointer"
          />
        </template>
      </SearchToolbar>
    </div>

    <div v-if="error" class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
      {{ error }}
    </div>

    <PaginateOrScroll
      v-else-if="hasLogs"
      :items="logs"
      :all-items="allItems"
      :loading-more="loadingMore"
      :has-more-items="hasMoreItems"
      :metadata="metadata"
      :selected-quantity="selectedQuantity"
      entity-name="log"
      @page-changed="handlePageChanged"
    >
      <template #table="{ items }">
        <OperationLogsTable
          :logs="items"
          :order-options="ORDER_OPTIONS"
          :is-filter-active="isFilterActive"
          :format-date="formatDate"
          :format-time="formatTime"
          :loading="showSkeleton"
          :refreshing="isRefreshing"
          :busy="loading"
          @update:sort="handleSortUpdate"
          @filter-clicked="openFilterModal"
        />
      </template>
    </PaginateOrScroll>

    <OperationLogsTable
      v-else
      :logs="[]"
      :order-options="ORDER_OPTIONS"
      :is-filter-active="isFilterActive"
      :format-date="formatDate"
      :format-time="formatTime"
      :is-filtered="hasActiveSearch || hasActiveFilters"
      :loading="showSkeleton"
      :refreshing="isRefreshing"
      :busy="loading"
      @update:sort="handleSortUpdate"
      @filter-clicked="openFilterModal"
    />
  </div>
</template>
